import React, { useEffect } from 'react';
import { change, Fields } from 'redux-form';
import { getFeatureConfig } from '../../../../userCustomization/featureToggle';
import { CAN_USE_RAMQ_CONTEXT_ELEMENTS_SELECTOR } from '../../../../userCustomization/featureNames';
import { ACT_FORM_NAME, actFormFields } from '../../../containers/ActForm/constants';
import NewContextElementsSelector from '../NewContextElementsSelector/NewContextElementsSelector';
import FormElement from '../../Form/FormElement/FormElement';
import LegacyContextElementsSelector from '../LegacyContextElementsSelector/LegacyContextElementsSelector';
import SelectedRamqContextElements from '../SelectedRamqContextElements/SelectedRamqContextElements';
import { useDispatch, useSelector } from 'react-redux';
import { selectSpecialty } from 'app/containers/User/selectors';
import { UserSpecialty } from 'shared/medyx-core/types/user';
import { getStore } from 'app/reduxStore/configureStore';
import { isExceptionalEmergencyDuringOffWeekPeriod } from 'app/validations/act/codes/exceptionalEmergencyDuringOffWeekPeriod/exceptionalEmergencyDuringOffWeekPeriod';
import { Act, ActContextElement } from 'shared/medyx-core/types/act';
import { UnknownAction } from '@reduxjs/toolkit';
import Restrict from 'application/components/restrict';
import Role from 'shared/domain/authentication/Role';
import RamqContextElementsField from 'app/components/Form/ActSpecificField/RamqContextElements/RamqContextElementsField';
import { Button } from '@material-ui/core';
import { FormattedMessage } from 'react-intl';
import { isUserAtLeast } from 'application/utilities/authentication';
import { RootState } from 'application/types/redux-state';

const IS_URGENT_AUTOMATIC_PER_SPECIALTY: Record<UserSpecialty, boolean> = {
  [UserSpecialty.ANESTHESIOLOGIST]: true,
  [UserSpecialty.ANESTHESIOLOGIST_INTENSIVIST]: true,
  [UserSpecialty.CARDIOLOGIST]: false,
  [UserSpecialty.ENDOCRINOLOGIST]: false,
  [UserSpecialty.GENERAL_SURGEON]: false,
  [UserSpecialty.INTERNIST]: false,
  [UserSpecialty.ORTHOPEDIC_SURGEON]: false,
  [UserSpecialty.OTOLARYNGOLOGIST]: false,
  [UserSpecialty.PNEUMOLOGIST]: false
};

export const ContextElementsSelector = ({ act }: { act: Act }) => {
  const dispatch = useDispatch();

  const userSpecialty = selectSpecialty()(getStore().getState());
  const userRole = useSelector<RootState, Role>((state) => state.authentication.user.role);

  useEffect(() => {
    const isUrgentRequired =
      !isUserAtLeast(userRole, Role.Agent) &&
      IS_URGENT_AUTOMATIC_PER_SPECIALTY[userSpecialty] &&
      isExceptionalEmergencyDuringOffWeekPeriod(act) &&
      !act.contextElements?.includes(ActContextElement.NON_URGENT);
    const hasUrgent = act.contextElements?.includes(ActContextElement.URGENT) ?? false;

    if (!hasUrgent && isUrgentRequired) {
      dispatch(
        change(ACT_FORM_NAME, `${actFormFields.contextElements}`, [
          ...(act.contextElements ?? []),
          ActContextElement.URGENT
        ]) as UnknownAction
      );
    } else if (hasUrgent && act.contextElements?.includes(ActContextElement.NON_URGENT)) {
      dispatch(
        change(ACT_FORM_NAME, `${actFormFields.contextElements}`, [
          ...act.contextElements.filter((element) => element !== ActContextElement.URGENT)
        ]) as UnknownAction
      );
    }
  }, [dispatch, act.contextElements, userSpecialty, act, userRole]);

  return (
    <>
      {getFeatureConfig(CAN_USE_RAMQ_CONTEXT_ELEMENTS_SELECTOR, false) ? (
        <Fields
          data-testid="context-elements-selector"
          names={[actFormFields.contextElements, actFormFields.ramqContextElements]}
          component={NewContextElementsSelector as React.ComponentType}
        />
      ) : (
        <FormElement
          data-testid="context-elements-selector"
          name={actFormFields.contextElements}
          component={LegacyContextElementsSelector}
        />
      )}
      <Restrict atLeastRole={Role.Agent}>
        <FormElement
          data-testid="ramq-context-elements-field"
          name={actFormFields.ramqContextElements}
          component={RamqContextElementsField}
          buttonRender={(onClick: () => void) => (
            <Button fullWidth variant="contained" onClick={onClick} style={{ margin: '10px' }}>
              Ajouter un élément de contexte RAMQ
            </Button>
          )}
        />
      </Restrict>
      <FormElement
        data-testid="ramq-context-elements-selector"
        name={actFormFields.ramqContextElements}
        component={SelectedRamqContextElements}
      />
    </>
  );
};

export default ContextElementsSelector;
